import { createContext, ReactNode, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { canAccessPaidFeatures } from '@linkie/api-client';
import { usePlaygroundRefetch } from '@/modules/playground/hooks/usePlaygroundRefetch.hook';
import { usePlaygroundState } from '@/modules/playground/store/playground.store';
import { useSettingsDialogStore } from '@/modules/playground/components/settings/store/settings-dialog.store';
import toastClient from '@/core/lib/toast-client';
import { PolarEmbedCheckout } from '@polar-sh/checkout/embed';

const PAID_ACCESS_SYNC_DELAYS_MS = [0, 1000, 2000, 3000, 4000, 5000, 5000, 5000, 5000];

type CheckoutEmbedProviderProps = {
  children: ReactNode;
};

export const CheckoutEmbedContext = createContext<
  | {
      checkoutInstance: PolarEmbedCheckout | null;
      openCheckout: (checkoutLink: string) => Promise<PolarEmbedCheckout | undefined>;
      closeCheckout: () => Promise<void>;
    }
  | undefined
>(undefined);

function CheckoutEmbedProvider({ children }: CheckoutEmbedProviderProps) {
  const queryClient = useQueryClient();
  const { refetchPage, refetchPageUsernames } = usePlaygroundRefetch();
  const pageId = usePlaygroundState((s) => s.payload._id) ?? '';
  const closeSettingsDialog = useSettingsDialogStore((s) => s.close);
  const [checkoutInstance, setCheckoutInstance] = useState<PolarEmbedCheckout | null>(null);

  const syncSubscriptionState = async () => {
    await Promise.allSettled([
      refetchPage(),
      refetchPageUsernames(),
      queryClient.invalidateQueries({ queryKey: ['subscriptions-by-user'] }),
      queryClient.invalidateQueries({ queryKey: ['can-access-paid-features'] }),
      queryClient.invalidateQueries({ queryKey: ['invoices'] }),
    ]);
  };

  const hasPaidAccess = async () => {
    const response = await queryClient
      .fetchQuery({
        queryKey: ['can-access-paid-features', pageId],
        queryFn: canAccessPaidFeatures,
        staleTime: 0,
      })
      .catch(() => undefined);

    return Boolean(response?.data?.canAccess);
  };

  const syncUntilPaidAccess = async () => {
    for (const delay of PAID_ACCESS_SYNC_DELAYS_MS) {
      if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
      if (await hasPaidAccess()) break;
    }

    await syncSubscriptionState();
  };

  const dismissCheckout = (checkout: PolarEmbedCheckout | null) => {
    checkout?.close();
    setCheckoutInstance(null);
    closeSettingsDialog();
  };

  const openCheckout = async (checkoutLink: string) => {
    const theme = 'dark';

    try {
      const checkout = await PolarEmbedCheckout.create(checkoutLink, theme);
      setCheckoutInstance(checkout);

      checkout.addEventListener('success', async (event) => {
        if (event.detail.redirect) return;

        dismissCheckout(checkout);
        toastClient.success('Payment successful. Your plan is now active.');
        await syncUntilPaidAccess();
      });

      checkout.addEventListener('close', async () => {
        await refetchPage();
      });

      return checkout;
    } catch (error) {
      console.error('Failed to open checkout', error);
    }
  };

  const closeCheckout = async () => {
    dismissCheckout(checkoutInstance);
    await syncSubscriptionState();
  };

  const value = {
    checkoutInstance,
    openCheckout,
    closeCheckout,
  };

  return <CheckoutEmbedContext.Provider value={value}>{children}</CheckoutEmbedContext.Provider>;
}

export default CheckoutEmbedProvider;
